// Panel de firma electrónica + impresión (Módulo H) — comparte el mismo
// mecanismo entre TODOS los grupos del catálogo (Grupos 1-4): el caller le
// pasa un `pdfBuilder` (armado con `generarPdfFormulario`/
// `generarPdfFormularioDePares` de pdfFormularioGenerico.ts, modo
// Questionnaire o modo pares planos) — este panel no sabe ni le importa
// cómo se armó el PDF, solo lo firma, lo persiste y ofrece verlo/
// imprimirlo. Mismo patrón de persistencia (Binary+DocumentReference) que
// ya usaba Formulario024Consentimiento.tsx en packages/hce.
import type { EspecificacionPdf } from '@logiacare/hce-pdf';
import type { DocumentReference, Reference } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { puedeAccionProfesional, useRolDelPracticante } from '@/lib/useRolDelPracticante';
import { firmarDocumentoEnServidor } from '../paciente/vaultClient';
import { EnvioDigitalDocumento } from './EnvioDigitalDocumento';

export interface PanelFirmaImpresionProps {
  readonly documentoId: string;
  readonly tipoDocumento: string;
  readonly titulo: string;
  readonly subject: { reference?: string };
  /**
   * Etiqueta del campo de identificación del profesional — por defecto
   * "Registro MSP/ACESS" (la mayoría de los formularios del Anexo 1 lo
   * exigen). El HCU-117 (Certificado Médico) pide explícitamente CÉDULA,
   * no código ACESS — pasar "Cédula" acá para ese caso puntual.
   */
  readonly etiquetaIdentificacion?: string;
  /**
   * Contenido del documento (2026-09-12): el PDF ya NO se arma acá — el
   * servidor lo arma y lo firma (`POST /api/hce/firmar-documento`), poniendo
   * él mismo nombre, registro y fecha del profesional. Esto solo describe el
   * contenido clínico.
   */
  readonly especificacionBuilder: () => Promise<EspecificacionPdf>;
  /**
   * Enganche para flujos que necesitan seguir procesando después de la
   * firma profesional (Grupo 2/024: arma el `Consent` referenciando este
   * `DocumentReference`) — se llama justo después de persistirlo, dentro
   * del mismo try/catch de `firmar()`, así que si tira error el panel
   * queda en su estado de error normal en vez de mostrar "listo" a medias.
   */
  readonly onFirmado?: (documentReference: DocumentReference) => Promise<void>;
  /**
   * Habilita el botón "Envío digital" en el paso 'listo' (Fase 4 de "receta
   * digital", pedido explícito del usuario: duplicar el mismo botón que ya
   * tiene RecetaWizard en las pestañas Documentos/Laboratorio y Estudios).
   * `undefined`/`false` por defecto — solo los llamantes que saben que su
   * formulario pertenece a esas pestañas lo activan (ver
   * FormularioDiscretoPage.tsx), no todo el catálogo (un consentimiento no
   * tiene sentido "enviarlo" por este mismo camino).
   */
  readonly envioDigitalHabilitado?: boolean;
  /**
   * Recurso clínico que este PDF documenta (2026-09-13). Va a
   * `DocumentReference.context.related`, el lugar estándar de R4 para "este
   * documento es sobre este recurso" — con search param `related`, así que
   * después se puede pedir `DocumentReference?related=<ref>` y encontrar el
   * PDF firmado desde el recurso. Hoy solo lo manda la nota de turno SOAPIE
   * (ver `onGuardado` en discretos/index.ts): un formulario que crea varios
   * recursos por guardado no tiene un único recurso que referenciar.
   */
  readonly relacionado?: Reference;
  /**
   * Como `relacionado`, pero el recurso se crea acá, DESPUÉS de que el servidor generó y firmó el PDF y antes de guardarlo (nota SOAPIE):
   * si la firma falla no queda un recurso clínico sin PDF. Debe ser idempotente: al reintentar la firma se vuelve a llamar.
   */
  readonly crearRelacionado?: () => Promise<Reference>;
  /**
   * Bloqueo adicional decidido por el caller (Fase 2: 001 exige filiación completa
   * del paciente antes de poder firmar — ver `camposFiliacionFaltantes` en
   * `paciente/filiacionCompleta.ts`). El mensaje/link de por qué vive en el propio
   * caller (mismo patrón que `faltaEpicrisisLink` en Formulario001Page.tsx) — acá
   * solo se pliega en `puedeFirmar`, sin acoplar este panel compartido a un
   * formulario puntual.
   */
  readonly bloqueadoExternamente?: boolean;
}

type Estado =
  | { fase: 'formulario' }
  | { fase: 'firmando' }
  | { fase: 'listo'; url: string; pdf: Uint8Array; simulada: boolean }
  | { fase: 'error'; mensaje: string };

export function PanelFirmaImpresion({
  documentoId,
  tipoDocumento,
  titulo,
  subject,
  etiquetaIdentificacion,
  especificacionBuilder,
  onFirmado,
  envioDigitalHabilitado,
  relacionado,
  crearRelacionado,
  bloqueadoExternamente,
}: PanelFirmaImpresionProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  // FEMO renderiza dos paneles a la vez (expediente 123 + certificado 081) —
  // bug real encontrado en verificación en vivo: el id fijo "registro-msp"
  // duplicaba el DOM y rompía el `label htmlFor` del segundo panel.
  const idRegistroMsp = useId();
  // Registro profesional (2026-09-12): lo decide el servidor
  // (hce_users.matricula, vía /api/hce/mi-rol) y se muestra en solo lectura —
  // /api/hce/firmar-documento ignora lo que mande el navegador. Excepción: 117 y
  // 116-cert piden CÉDULA (`etiquetaIdentificacion`), un dato que el servidor
  // no tiene: ahí se sigue tipeando, solo para el cuerpo del PDF; el sello de
  // la firma igual lleva la matrícula, y sin ella no se puede firmar.
  const rolEstado = useRolDelPracticante();
  const matricula = rolEstado.cargando ? null : rolEstado.matricula;
  const pideCedula = etiquetaIdentificacion !== undefined;
  const [cedula, setCedula] = useState('');
  const identificacion = pideCedula ? cedula.trim() : (matricula ?? '');
  const puedeFirmar = matricula !== null && identificacion !== '' && !bloqueadoExternamente;
  const [estado, setEstado] = useState<Estado>({ fase: 'formulario' });
  // "Ver/imprimir" (2026-09-14): ver comentario en RecetaWizard.tsx — un
  // <a target="_blank">/window.open() a una URL blob: se bloquea en la
  // mayoría de navegadores; se muestra en un <iframe> dentro de la misma
  // página en su lugar (con el botón de imprimir nativo del visor de PDF).
  const [mostrarPreview, setMostrarPreview] = useState(false);

  async function firmar(): Promise<void> {
    if (!puedeFirmar) return;
    setEstado({ fase: 'firmando' });
    try {
      const especificacion = await especificacionBuilder();
      const firmado = await firmarDocumentoEnServidor({
        documentoId,
        tipoDocumento,
        especificacion,
        // Solo 117/116-cert: la cédula del profesional, que el servidor no
        // tiene. El servidor la acepta únicamente para esos tipoDocumento.
        cedula: pideCedula ? identificacion : undefined,
      });

      const recursoRelacionado = crearRelacionado ? await crearRelacionado() : relacionado;
      const binario = await medplum.createBinary({
        data: firmado.signedPdf,
        contentType: 'application/pdf',
        filename: `${tipoDocumento}-firmado.pdf`,
        securityContext: subject.reference ? { reference: subject.reference } : undefined, // MT-042: el paciente, para la regla de vínculo asistencial
      });
      const docRef = await medplum.createResource<DocumentReference>({
        resourceType: 'DocumentReference',
        status: 'current',
        subject: subject.reference ? { reference: subject.reference } : undefined,
        type: { text: titulo },
        author: profile ? [{ reference: `${profile.resourceType}/${profile.id}` }] : [],
        content: [
          {
            attachment: {
              contentType: 'application/pdf',
              url: `Binary/${binario.id}`,
              title: firmado.simulada ? `${titulo} — FIRMA SIMULADA (Security Data no configurado)` : `${titulo} firmado (Security Data)`,
            },
          },
        ],
        context: recursoRelacionado ? { related: [recursoRelacionado] } : undefined,
        extension: [
          { url: 'http://logiacare.ec/fhir/extension/firma-signature-ref', valueString: firmado.signatureRef },
          { url: 'http://logiacare.ec/fhir/extension/firma-firmado-en', valueDateTime: firmado.firmadoEn },
          { url: 'http://logiacare.ec/fhir/extension/firma-simulada', valueBoolean: firmado.simulada },
        ],
      });
      await onFirmado?.(docRef);

      // Blob URL, nunca descarga automática — el médico decide si la abre.
      const blob = new Blob([firmado.signedPdf as BlobPart], { type: 'application/pdf' });
      setEstado({ fase: 'listo', url: URL.createObjectURL(blob), pdf: firmado.signedPdf, simulada: firmado.simulada });
    } catch (err) {
      setEstado({ fase: 'error', mensaje: err instanceof Error ? err.message : String(err) });
    }
  }

  if (estado.fase === 'listo') {
    return (
      <div className="flex flex-col gap-2 rounded-md border border-border p-3">
        <Alert>
          <AlertTitle>{t('formulario.firma.listoTitulo')}</AlertTitle>
          <AlertDescription>{estado.simulada ? t('formulario.firma.listoSimulada') : t('formulario.firma.listoDetalle')}</AlertDescription>
        </Alert>
        <div className="flex items-center gap-2">
          <Button type="button" variant="outline" size="sm" className="w-fit" onClick={() => setMostrarPreview((v) => !v)}>
            {t('formulario.firma.verImprimir')}
          </Button>
          <Button asChild variant="outline" size="sm" className="w-fit">
            <a href={estado.url} download={`${tipoDocumento}.pdf`}>
              {t('formulario.firma.descargar')}
            </a>
          </Button>
        </div>
        {mostrarPreview && <iframe src={estado.url} title={t('formulario.firma.verImprimir')} className="h-[70vh] w-full rounded-md border border-border" />}
        {envioDigitalHabilitado && puedeAccionProfesional(rolEstado, 'documento-al-paciente') && <EnvioDigitalDocumento subject={subject} pdf={estado.pdf} fileName={`${tipoDocumento}.pdf`} etiquetaDocumento={titulo} />}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border p-3">
      <Label htmlFor={idRegistroMsp} className="block text-xs font-mono uppercase tracking-wide text-hce-muted">
        {etiquetaIdentificacion ?? t('formulario.firma.registroMspLabel')}
      </Label>
      {pideCedula ? (
        <Input id={idRegistroMsp} value={cedula} onChange={(e) => setCedula(e.target.value)} className="max-w-xs" />
      ) : (
        <Input
          id={idRegistroMsp}
          value={matricula ?? ''}
          readOnly
          placeholder={rolEstado.cargando ? t('formulario.firma.cargandoMatricula') : ''}
          className="max-w-xs"
        />
      )}
      {!rolEstado.cargando && matricula === null && (
        <Alert variant="red">
          <AlertDescription>{t('formulario.firma.sinMatricula')}</AlertDescription>
        </Alert>
      )}
      {estado.fase === 'error' && (
        <Alert variant="red">
          <AlertTitle>{t('formulario.firma.error')}</AlertTitle>
          <AlertDescription>{estado.mensaje}</AlertDescription>
        </Alert>
      )}
      <Button type="button" size="sm" className="w-fit" disabled={estado.fase === 'firmando' || !puedeFirmar} onClick={() => void firmar()}>
        {estado.fase === 'firmando' ? t('formulario.firma.firmando') : t('formulario.firma.boton')}
      </Button>
    </div>
  );
}
